import { type IntlShape, defineMessage } from 'react-intl'
import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import { isInsuranceProtectionItem, isLuxPlusSubscriptionItem, isSubscriptionJoinItem } from 'lib/checkout/checkoutUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { priceBreakdownTitleMessages } from 'checkout/constants/priceBreakdownTitles'

type BookingInfoItem = App.Checkout.PaymentScheduleRequestInfo['bookingInfo']['items'][number]

/**
 * Lead-only items are paid upfront by the lead and never split with the group: the LuxPlus
 * membership and travel insurance. Everything else in a Group Payment cart is split.
 * Mirrors svc-payment's GROUP_PAYMENT_LEAD_ONLY_ITEM_TYPES. Adding a lead-only type also needs
 * svc-payment's Group Payment calculation extended (see GROUP_PAYMENT_LEAD_ONLY_ITEM_TYPES).
 */
export type GroupPaymentLeadOnlyItemType = 'luxPlusMembership' | 'insurance'

export interface GroupPaymentLeadOnlyItem {
  type: GroupPaymentLeadOnlyItemType
  amount: number
}

// Checks cart items. Group Payment eligibility runs on the cart before the booking items exist
// (getGroupPaymentPreviewRequestInfo depends on it), so it can't use isGroupPaymentLeadOnlyBookingItem.
export function isGroupPaymentLeadOnlyCartItem(item: App.Checkout.AnyItem): boolean {
  // Legacy booking protection isn't an insurance item, so it still blocks Group Payment.
  return isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item) || isInsuranceProtectionItem(item)
}

export function isGroupPaymentLeadOnlyBookingItem(item: BookingInfoItem): boolean {
  return item.type === 'subscription' || item.type === 'insurance'
}

function toLuxPlusMembershipItems(amount: number): Array<GroupPaymentLeadOnlyItem> {
  const membershipAmount = floatify(amount)
  return membershipAmount > 0 ? [{ type: 'luxPlusMembership', amount: membershipAmount }] : []
}

function toInsuranceItems(amount: number): Array<GroupPaymentLeadOnlyItem> {
  const insuranceAmount = floatify(amount)
  return insuranceAmount > 0 ? [{ type: 'insurance', amount: insuranceAmount }] : []
}

export function getGroupPaymentLeadOnlyBookingItems(items: Array<BookingInfoItem>): Array<GroupPaymentLeadOnlyItem> {
  const payableAmount = (item: BookingInfoItem) => item.total - item.discountTotal
  return [
    ...toLuxPlusMembershipItems(sum(items.filter(item => item.type === 'subscription'), payableAmount)),
    ...toInsuranceItems(sum(items.filter(item => item.type === 'insurance'), payableAmount)),
  ]
}

export function getGroupPaymentLeadOnlyOrderItems(order: App.Order): Array<GroupPaymentLeadOnlyItem> {
  // Booking-change reissues (change_dates, change_package, extend_dates, reduce_dates) keep the
  // cancelled item in insuranceItems, because orderMap only drops insurance_upgraded. Group Payment
  // orders block those changes today. If they're ever allowed, drop cancelled items with those
  // reasons here and in the Group Payment order total (see OrderInsuranceSection).
  return [
    ...toLuxPlusMembershipItems(sum(order.subscriptionItems, item => item.total)),
    ...toInsuranceItems(sum(order.insuranceItems, item => item.total)),
  ]
}

export function sumGroupPaymentLeadOnlyItems(items: Array<GroupPaymentLeadOnlyItem>): number {
  return floatify(sum(items, item => item.amount))
}

/**
 * Lead-only items credits and gift cards can't pay for. Only the LuxPlus membership:
 * insurance can be paid with credits, same as svc-payment.
 */
export function sumGroupPaymentCreditIneligibleLeadOnlyItems(items: Array<GroupPaymentLeadOnlyItem>): number {
  return sumGroupPaymentLeadOnlyItems(items.filter(item => item.type === 'luxPlusMembership'))
}

const luxPlusMembershipLabel = defineMessage({
  id: 'jwn63LZN9HniNSCamUN8',
  defaultMessage: '{programName} Membership',
  description: 'Label for the LuxPlus membership line paid by the lead outside the group split on a group booking payment summary',
})

export function formatGroupPaymentLeadOnlyLabel(
  intl: IntlShape,
  type: GroupPaymentLeadOnlyItemType,
  insuranceProductName: string,
): string {
  switch (type) {
    case 'luxPlusMembership':
      return intl.formatMessage(luxPlusMembershipLabel, { programName: LUXURY_PLUS.PROGRAM_NAME })
    case 'insurance':
      return intl.formatMessage(priceBreakdownTitleMessages.travelInsurance, { insuranceProductName })
  }
}
